// src/components/Navbar.js
import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
    setMenuOpen(false);
  };

  const isActive = (path) => pathname === path;

  const linkStyle = (path) => ({
    color: isActive(path) ? "#0B1E3D" : "#6B7280",
    fontWeight: isActive(path) ? 600 : 500,
    fontSize: 14, background: "none", border: "none",
    cursor: "pointer", fontFamily: "'Inter',sans-serif",
    padding: "4px 0", textDecoration: "none",
    borderBottom: isActive(path) ? "2px solid #00C6D7" : "2px solid transparent",
    transition: "all .15s", whiteSpace: "nowrap",
  });

  const mobileLinkStyle = (path) => ({
    display: "block", padding: "14px 20px",
    color: isActive(path) ? "#00A896" : "#374151",
    fontWeight: isActive(path) ? 600 : 500,
    fontSize: 15, textDecoration: "none",
    borderBottom: "1px solid #F0F4F8",
    background: isActive(path) ? "rgba(0,198,215,.05)" : "transparent",
    fontFamily: "'Inter',sans-serif",
  });

  return (
    <>
      <nav style={{
        position: "sticky", top: 0, zIndex: 100,
        background: "rgba(255,255,255,.97)", backdropFilter: "blur(10px)",
        borderBottom: "1px solid #E2EBF0",
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>

          {/* Logo */}
          <Link to="/" style={{ display: "flex", alignItems: "center", flexShrink: 0 }} onClick={() => setMenuOpen(false)}>
            <img src="/logo.jpeg" alt="Pulse Logistics"
              style={{ height: 30, width: "auto", objectFit: "contain" }}
              onError={e => { e.target.style.display = "none"; }} />
          </Link>

          {/* Desktop Nav Links */}
          <div style={{ display: "flex", alignItems: "center", gap: 24 }} className="desktop-nav">
            <Link to="/"        style={linkStyle("/")}>Home</Link>
            <Link to="/about"   style={linkStyle("/about")}>About</Link>
            <Link to="/track"   style={linkStyle("/track")}>Track Order</Link>
            <Link to="/request" style={linkStyle("/request")}>Request Pickup</Link>
            {user?.role === "admin" && <>
              <Link to="/admin"     style={linkStyle("/admin")}>Dashboard</Link>
              <Link to="/analytics" style={linkStyle("/analytics")}>Analytics</Link>
            </>}
            {user?.role === "rider"            && <Link to="/rider" style={linkStyle("/rider")}>Rider Portal</Link>}
            {user?.role === "customer_service" && <Link to="/cs"    style={linkStyle("/cs")}>CS Portal</Link>}
          </div>

          {/* Right side */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {/* Desktop auth */}
            <div className="desktop-nav" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {!user ? (
                <>
                  <Link to="/login"   className="btn btn-ghost btn-sm">Log in</Link>
                  <Link to="/request" className="btn btn-primary btn-sm">Get Started</Link>
                </>
              ) : (
                <>
                  <span className={`tag ${user.role === "admin" ? "tag-cyan" : user.role === "rider" ? "tag-amber" : "tag-navy"}`}>
                    {user.role === "admin" ? "⚙️ Admin" : user.role === "rider" ? "🏍️ Rider" : "🎧 CS"}
                  </span>
                  <button className="btn btn-ghost btn-sm" onClick={handleLogout}>Logout</button>
                </>
              )}
            </div>

            {/* Hamburger */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={{ display: "none", flexDirection: "column", gap: 5, background: "none", border: "none", cursor: "pointer", padding: 6 }}
              className="hamburger">
              <span style={{ width: 22, height: 2, background: menuOpen ? "#00A896" : "#0B1E3D", borderRadius: 2, transition: "all .2s", transform: menuOpen ? "rotate(45deg) translate(5px,5px)" : "none" }} />
              <span style={{ width: 22, height: 2, background: "#0B1E3D", borderRadius: 2, opacity: menuOpen ? 0 : 1, transition: "all .2s" }} />
              <span style={{ width: 22, height: 2, background: menuOpen ? "#00A896" : "#0B1E3D", borderRadius: 2, transition: "all .2s", transform: menuOpen ? "rotate(-45deg) translate(5px,-5px)" : "none" }} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div style={{ background: "#fff", borderTop: "1px solid #E2EBF0", boxShadow: "0 8px 24px rgba(0,0,0,.08)" }}>
            <Link to="/"        style={mobileLinkStyle("/")}        onClick={() => setMenuOpen(false)}>🏠 Home</Link>
            <Link to="/about"   style={mobileLinkStyle("/about")}   onClick={() => setMenuOpen(false)}>ℹ️ About Us</Link>
            <Link to="/track"   style={mobileLinkStyle("/track")}   onClick={() => setMenuOpen(false)}>🔍 Track Order</Link>
            <Link to="/request" style={mobileLinkStyle("/request")} onClick={() => setMenuOpen(false)}>📦 Request Pickup</Link>
            {user?.role === "admin" && <>
              <Link to="/admin"     style={mobileLinkStyle("/admin")}     onClick={() => setMenuOpen(false)}>⚙️ Dashboard</Link>
              <Link to="/analytics" style={mobileLinkStyle("/analytics")} onClick={() => setMenuOpen(false)}>📊 Analytics</Link>
            </>}
            {user?.role === "rider"            && <Link to="/rider" style={mobileLinkStyle("/rider")} onClick={() => setMenuOpen(false)}>🏍️ Rider Portal</Link>}
            {user?.role === "customer_service" && <Link to="/cs"    style={mobileLinkStyle("/cs")}    onClick={() => setMenuOpen(false)}>🎧 CS Portal</Link>}

            <div style={{ padding: "14px 20px", display: "flex", gap: 10 }}>
              {!user ? (
                <>
                  <Link to="/login" className="btn btn-ghost" style={{ flex: 1, justifyContent: "center" }} onClick={() => setMenuOpen(false)}>Log in</Link>
                  <Link to="/request" className="btn btn-primary" style={{ flex: 1, justifyContent: "center" }} onClick={() => setMenuOpen(false)}>Get Started</Link>
                </>
              ) : (
                <button className="btn btn-ghost btn-full" onClick={handleLogout}>Logout</button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Responsive styles injected */}
      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .hamburger { display: flex !important; }
        }
        @media (min-width: 769px) {
          .hamburger { display: none !important; }
          .desktop-nav { display: flex !important; }
        }
      `}</style>
    </>
  );
}